import {
  DetailedHTMLProps,
  InputHTMLAttributes,
  ReactNode,
  useState,
} from "react";
import { useId } from "react";

import styles from "./TextInput.module.scss";
import { cn } from "../../utils/utils";

export type TextInputProps = {
  icon?: ReactNode;
  placeholder?: string;
  fieldName?: string;
  label?: string;
  hintIcon?: ReactNode;
  inputValue?: string;
  setInputValue?: (value: string) => void;
  simple?: boolean;
  onSubmit?: () => void;
  disabled?: boolean;
  isError?: boolean;
  errorText?: string;
  crypted?: boolean;
  withPasswordToggle?: boolean;
  passwordVisibleIcon?: ReactNode;
  passwordHiddenIcon?: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  small?: boolean;
  name?: string;
  inputProps?: Partial<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
  > &
    Record<`data-${string}`, string>;
  className?: string;
};

export const TextInput = ({
  icon,
  placeholder,
  fieldName,
  label,
  hintIcon,
  inputValue,
  setInputValue,
  simple,
  onSubmit,
  disabled,
  isError,
  errorText,
  crypted,
  withPasswordToggle,
  passwordVisibleIcon,
  passwordHiddenIcon,
  onKeyDown,
  small,
  inputProps,
  className,
}: TextInputProps): JSX.Element => {
  const inputId = useId();
  const effectiveFieldLabel = label ?? fieldName;
  const [passwordVisible, setPasswordVisible] = useState(false);

  const resolveInputType = (): string => {
    if (!crypted) return "text";
    if (withPasswordToggle && passwordVisible) return "text";
    return "password";
  };

  return (
    <div className={cn(styles.text_input_wrapper)}>
      {effectiveFieldLabel && (
        <div className={styles.field_caption}>
          <label className={styles.field_name} htmlFor={inputId}>
            {effectiveFieldLabel}
          </label>
          {hintIcon && <span className={styles.hint_icon}>{hintIcon}</span>}
        </div>
      )}

      <div
        className={cn(
          styles.text_input_container,
          simple && styles.simple,
          disabled && styles.disabled,
          small && styles.small,
          isError && styles.error,
          className,
        )}
      >
        <input
          id={inputId}
          className={styles.text_input}
          type={resolveInputType()}
          value={inputValue}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onSubmit) {
              onSubmit();
            }
            onKeyDown?.(e);
          }}
          {...(inputProps ?? {})}
          onChange={(e) => {
            setInputValue?.(e.target.value);
            inputProps?.onChange?.(e);
          }}
        />

        {crypted && withPasswordToggle && (
          <button
            type="button"
            className={styles.password_toggle}
            onClick={() => setPasswordVisible((prev) => !prev)}
            tabIndex={-1}
            aria-label={passwordVisible ? "Hide password" : "Show password"}
          >
            {passwordVisible ? passwordVisibleIcon : passwordHiddenIcon}
          </button>
        )}

        {!simple && icon && <div className={styles.icon_wrapper}>{icon}</div>}
      </div>

      {isError && errorText && <p className={styles.error_text}>{errorText}</p>}
    </div>
  );
};
